<template lang="pug">
.carbon-footprint(ref="carbonFootprintEl")
  //- Chart
  .chart.cards
    .title Carbon footprint
    .carbon-footprint-chart-wrapper
      TreesChart(
        v-if="settings?.recommendationsPageOptions?.co2Chart === Co2Chart.Trees"
        :treeCount="eachDisplayTreeCount"
        :currentRating="report?.beforeRating"
        :newRating="report?.afterRating"
        ref="treesChartEl"
      )
      CarbonFootprintChart(
        v-else-if="report?.beforeRating && report?.afterRating"
        :currentRating="report?.beforeRating",
        :newRating="report?.afterRating",
        ref="treesChartEl",
        :emissionBefore="emissionBefore",
        :emissionAfter="emissionAfter"
      )
  //- Emission info
  .emission-infos
    .differences.cards
      .header {{ $t('carbonFootprint.emissionInfo.title') }}
      .body
        table
          tbody
            tr.item
              td.label {{ $t('carbonFootprint.emissionInfo.before') }}
              td.value {{ emissionBefore }}
              td.label {{ $t('carbonFootprint.emissionInfo.tonnesY') }}
            tr.item
              td.label {{ $t('carbonFootprint.emissionInfo.after') }}
              td.value {{ emissionAfter }}
              td.label {{ $t('carbonFootprint.emissionInfo.tonnesY') }}
            tr.item
              td.label {{ $t('carbonFootprint.emissionInfo.reduction') }}
              td.value {{ emissionDifference }}
              td.label {{ $t('carbonFootprint.emissionInfo.tonnesY') }}

  //- After Upgrade Info
  .absorbed-trees-card
    .description.cards
      .card-icon
        GammaIcon(:src="getIconFromCSS('co2')" default-src="Co2Icon")
      .absorbed-trees(v-safe-html="$t('carbonFootprint.absorbedTrees', { emissionDifference, trees})")


  //- C02 info
  .co2-card
    .description.cards
      .card-icon
        GammaIcon(:src="getIconFromCSS('tree')" :default-src="TreeIcon")
      .co2(v-safe-html="$t('carbonFootprint.co2', {emissionBefore, emissionAfter, trees})")

</template>

<script lang="ts">
import {useRecommendations, useSettings} from "@/store";
import {storeToRefs} from "pinia";
import {computed, defineComponent, ref} from "vue";
import CarbonFootprintChart from "./CarbonFootprintChart.vue";
import {refThrottled, useElementSize} from "@vueuse/core";
import {ApplyRecommendationsReport, Co2Chart} from "@/types";
import {formatNumberToString, getIconFromCSS, getWidthPercentage} from "@/helpers";
import Card from "primevue/card";
import GammaIcon from "@/components/icons/GammaIcon.vue";
import TreesChart from "@/components/result/carbon-footprint/TreesChart.vue";
import TreeIcon from "@/components/icons/carbon-footprint/tree-icon.vue";
import Co2Icon from "@/components/icons/carbon-footprint/co2-icon.vue";

export default defineComponent({
  components: {
    TreesChart,
    CarbonFootprintChart,
    GammaIcon,
    Card,
    TreeIcon,
    Co2Icon,
  },
  setup() {
    const {report} = storeToRefs(useRecommendations());
    const {settings} = storeToRefs(useSettings());

    const treesChartEl = ref<HTMLDivElement | null>(null);
    const carbonFootprintEl = ref<HTMLDivElement | null>(null);

    const {width} = useElementSize(treesChartEl);
    const widthThrottled = refThrottled(width, 1000);
    const displayTreesNum = computed(() => Math.round(widthThrottled.value / 75));

    const emissionBefore = computed(() =>
        formatNumberToString(
            report.value ? report.value.carbon.co2EmissionsBefore / 1e3 : 0,
            {style: "decimal", maximumFractionDigits: 2}
        )
    );
    const emissionAfter = computed(() =>
        formatNumberToString(
            report.value ? report.value.carbon.co2EmissionsAfter / 1e3 : 0,
            {style: "decimal", maximumFractionDigits: 2}
        )
    );
    const emissionDifference = computed(() =>
        formatNumberToString(
            report.value
                ? (report.value.carbon.co2EmissionsBefore -
                    report.value.carbon.co2EmissionsAfter) /
                1e3
                : 0,
            {style: "decimal", maximumFractionDigits: 2}
        )
    );

    const trees = computed(() =>
        formatNumberToString(report.value ? report.value.carbon.trees : 0, {
          style: "decimal",
          maximumFractionDigits: 0,
        })
    );

    const eachDisplayTreeCount = computed(() =>
        report.value ? calcEachDisplayTreeCount(report.value, displayTreesNum.value) : 0
    );

    function calcEachDisplayTreeCount(
        report: ApplyRecommendationsReport,
        displayedTrees: number
    ): number {
      const beforeW = getWidthPercentage(report.beforeRating.value);
      const afterW = getWidthPercentage(report.afterRating.value);
      const diffInPercentage = Math.abs(beforeW - afterW);

      const maxTrees = report.carbon.trees / (diffInPercentage / 100);
      return maxTrees / displayedTrees != 0 ? displayedTrees : 1;
    }

    return {
      treesChartEl,
      carbonFootprintEl,
      report,
      displayTreesNum,
      eachDisplayTreeCount,
      emissionBefore,
      emissionAfter,
      emissionDifference,
      trees,
      formatNumberToString,
      getIconFromCSS,
      settings,
      Co2Chart,
      TreeIcon,
      Co2Icon,
    };
  },
});
</script>

<style lang="scss" scoped>
.carbon-footprint {
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: calc(100% - 0.75rem);

  .cards {
    background-color: $background-color-default;
    border: $bar-border;

    .title {
      text-transform: uppercase;
      padding: 1rem 1rem 0.25rem 1rem;
      font-weight: bolder;
      color: $primary-color;
    }
  }

  .chart {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .absorbed-trees-card {
    display: flex;
    width: calc(60% - 0.75rem);
    gap: 0;

    &:before {
      content: "";
      width: 1rem;
      background-color: $primary-color;
    }

    .card-icon {
      display: none;
    }
  }

  .co2-card {
    display: flex;

    .description {
      gap: 5rem;
    }
  }

  .emission-infos {
    flex-wrap: wrap;
    display: flex;

    &:before {
      content: "";
      width: 0.25rem;
      background-color: $secondary-color;
    }

    table {
      .item {
        td {
          display: flex;
          width: 100%;

          &:last-child {
            width: min-content;
          }
        }

        .value {
          justify-content: end;
          padding-right: 1rem;
        }
      }
    }

    .header {
      padding: 1.25rem 1.75rem;
      color: $secondary-color;
      font-size: 1.3rem;
      font-weight: 600;
    }

    & > div {
      flex: 1;
    }

    .cards {
      padding: 0;
      display: flex;
      flex-direction: column;
      width: calc(100% - 0.25rem);

      .body {
        padding: 2rem 3rem;
      }
    }

    .differences {
      .body {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 1rem;
        font-size: 1.75rem;
        align-items: inherit;

        .item {
          display: flex;
          justify-content: space-between;
          color: $primary-color;
          align-items: center;

          .label {
            font-weight: 500;

            &:first-child {
              padding-right: 1rem;
            }
          }

          .value {
            font-weight: 600;
          }
        }
      }
    }
  }

  .description {
    display: flex;
    align-items: center;
    padding: 3.5rem;
    color: $primary-color;

    & > div {
      flex: 1;
    }

    svg {
      $size: 5rem;
      width: $size;
      height: $size;
    }

    span {
      font-size: 1.8rem;
      max-width: 50rem;
    }

    .absorbed-trees {
      font-size: 1.5rem;
      line-height: 2rem;
    }

    .co2 {
      font-size: 1.25rem;
      line-height: 1.8rem;

      div:first-child {
        margin-bottom: 0.5rem;
      }
    }
  }
}

.card-icon {
  max-width: 5rem;
}

.carbon-footprint-chart-wrapper {
  padding-top: 2rem;
}

@media screen and (max-width: 650px) {
  .carbon-footprint {
    .description {
      svg {
        display: none;
      }
    }
  }
}

.carbon-footprint {
  display: grid;
  grid-template-columns: 40% 60%;
  grid-template-rows: auto auto auto;
  gap: 0.75rem;
  grid-column-gap: 0.75rem;

  .chart {
    grid-column: 1 / span 2;
    width: 100%;
  }

  .emission-infos {
    grid-column: 1;
    grid-row: 2;
    width: 100%;
  }

  .absorbed-trees-card {
    grid-column: 2;
    grid-row: 2;
    width: 100%;
  }

  .co2-card {
    grid-column: 1 / span 2;
    grid-row: 3;
    width: 100%;
  }

  @media screen and (max-width: 1000px) {
    .chart,
    .emission-infos,
    .absorbed-trees-card,
    .co2-card {
      grid-column: 1 / span 2;
      width: 100%;
    }

    .absorbed-trees-card {
      grid-row: 3;
    }
    .co2-card {
      grid-row: 4;
    }
  }
}

@media screen and (min-width: 1200px) {
  .carbon-footprint {
    max-width: calc(100% - 0.75rem);
  }
}
</style>
